/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import type { AuthController } from '@/presentation/controllers/auth-controller.js';
import { applyAuthTheme } from '@/presentation/hooks/apply-auth-theme.js';
import { useApp } from '@/presentation/hooks/use-app.js';

type State = {
    isLoading: boolean;
    error: string | null;
};

export function useAuth(controller: AuthController) {
    const { useAuthStore, useThemeStore } = useApp();
    const user = useAuthStore((s) => s.user);
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const setAuth = useAuthStore((s) => s.setAuth);
    const clearAuth = useAuthStore((s) => s.clearAuth);
    const setLoading = useAuthStore((s) => s.setLoading);
    const setUser = useAuthStore((s) => s.setUser);
    const storeIsLoading = useAuthStore((s) => s.isLoading);
    const applyClientTheme = useThemeStore((s) => s.applyClientTheme);
    const resetToDefaultTheme = useThemeStore((s) => s.resetToDefaultTheme);

    const [state, setState] = useState<State>({ isLoading: false, error: null });

    const login = useCallback(
        async (email: string, password: string) => {
            setLoading(true);
            setState({ isLoading: false, error: null });
            const result = await controller.login(email, password);
            result.fold(
                (error) => {
                    setState({ isLoading: false, error: error.message });
                    setLoading(false);
                },
                (data) => {
                    setAuth(data.user, data.accessToken, data.refreshToken);
                    applyAuthTheme(data.theme, applyClientTheme, resetToDefaultTheme);
                    setState({ isLoading: false, error: null });
                }
            );
            return result.isRight();
        },
        [controller, setAuth, setLoading, applyClientTheme, resetToDefaultTheme]
    );

    const redeemMicrosoft = useCallback(
        async (ticket: string) => {
            setLoading(true);
            setState({ isLoading: false, error: null });
            const result = await controller.redeemEntraTicket(ticket);
            result.fold(
                (error) => {
                    setState({ isLoading: false, error: error.message });
                    setLoading(false);
                },
                (data) => {
                    setAuth(data.user, data.accessToken, data.refreshToken);
                    applyAuthTheme(data.theme, applyClientTheme, resetToDefaultTheme);
                    setState({ isLoading: false, error: null });
                }
            );
            return result.isRight();
        },
        [controller, setAuth, setLoading, applyClientTheme, resetToDefaultTheme]
    );

    const logout = useCallback(async () => {
        await controller.logout();
        clearAuth();
        resetToDefaultTheme();
    }, [controller, clearAuth, resetToDefaultTheme]);

    const getMe = useCallback(async () => {
        const result = await controller.getMe();
        result.fold(
            () => {},
            (data) => {
                setUser(data.user);
                applyAuthTheme(data.theme, applyClientTheme, resetToDefaultTheme);
            }
        );
        return result;
    }, [controller, setUser, applyClientTheme, resetToDefaultTheme]);

    const forgotPassword = useCallback(
        async (email: string) => {
            setState((s) => ({ ...s, isLoading: true, error: null }));
            const result = await controller.forgotPassword(email);
            result.fold(
                (error) => setState({ isLoading: false, error: error.message }),
                () => setState({ isLoading: false, error: null })
            );
            return result.isRight();
        },
        [controller]
    );

    const resetPassword = useCallback(
        async (token: string, newPassword: string) => {
            setState((s) => ({ ...s, isLoading: true, error: null }));
            const result = await controller.resetPassword(token, newPassword);
            result.fold(
                (error) => setState({ isLoading: false, error: error.message }),
                () => setState({ isLoading: false, error: null })
            );
            return result.isRight();
        },
        [controller]
    );

    const validateResetToken = useCallback(
        async (token: string) => {
            setState((s) => ({ ...s, isLoading: true, error: null }));
            const result = await controller.validateResetToken(token);
            let valid = false;
            result.fold(
                () => {
                    setState({ isLoading: false, error: null });
                    valid = false;
                },
                (v) => {
                    setState({ isLoading: false, error: null });
                    valid = v;
                }
            );
            return valid;
        },
        [controller]
    );

    const firstAccess = useCallback(
        async (token: string, newPassword: string) => {
            setState((s) => ({ ...s, isLoading: true, error: null }));
            const result = await controller.firstAccess(token, newPassword);
            result.fold(
                (error) => setState({ isLoading: false, error: error.message }),
                () => setState({ isLoading: false, error: null })
            );
            return result.isRight();
        },
        [controller]
    );

    const changePassword = useCallback(
        async (currentPassword: string, newPassword: string, confirmPassword: string) => {
            setState((s) => ({ ...s, isLoading: true, error: null }));
            const result = await controller.changePassword(currentPassword, newPassword, confirmPassword);
            result.fold(
                (error) => setState({ isLoading: false, error: error.message }),
                () => setState({ isLoading: false, error: null })
            );
            return result.isRight();
        },
        [controller]
    );

    useEffect(() => {
        if (isAuthenticated && !user) {
            getMe();
        }
    }, [isAuthenticated, user, getMe]);

    return {
        user,
        isAuthenticated,
        isLoading: state.isLoading || storeIsLoading,
        error: state.error,
        login,
        redeemMicrosoft,
        logout,
        getMe,
        forgotPassword,
        resetPassword,
        validateResetToken,
        firstAccess,
        changePassword
    };
}
